<template>
    <VueDatePicker
        v-if="isMounted"
        ref="dpRef"
        v-model="internalDate"
        :format="formatDateForDisplay"
        :preview-format="formatDateForDisplay"
        :min-date="resolvedMinDate"
        :max-date="resolvedMaxDate"
        :month-picker="isMonthPicker"
        :auto-apply="true"
        :time-config="{ enableTimePicker: false }"
        :text-input="{ format: displayFormat }"
        :teleport="true"
        :floating="{ placement: 'bottom-start' }"
        v-bind="filteredAttrs"
        @update:model-value="onDateChange"
        @open="$emit('on-open')"
        @closed="$emit('on-close')"
    >
        <!-- <template #input-icon>
            <svg
                class="dp__input_icon w-4 h-4 text-slate-800 pointer-events-none"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
        </template> -->
        <template #dp-input="{ value }">
            <div class="relative w-full">
                <input
                    ref="inputRef"
                    type="text"
                    :value="formattedInputValue"
                    v-bind="inputAttrs"
                    readonly
                    class="w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 pr-3 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:outline-none focus:border-slate-800 hover:border-slate-800 cursor-pointer"
                />
            </div>
        </template>
    </VueDatePicker>
</template>

<script setup>
import { ref, computed, onMounted, watch, useAttrs } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
    flatpickrFormatToDayjs,
    resolveDateRef,
} from '@/utils/date-format-mapper';

dayjs.extend(customParseFormat);

defineOptions({
    inheritAttrs: false,
});

const props = defineProps({
    modelValue: {
        type: [String, Date, Array, Object],
        default: '',
    },
    config: {
        type: Object,
        default: () => ({}),
    },
    monthPicker: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    'update:modelValue',
    'on-change',
    'on-open',
    'on-close',
]);

const attrs = useAttrs();
const dpRef = ref(null);
const inputRef = ref(null);
const isMounted = ref(false);

const isMonthPicker = computed(() => {
    if (props.monthPicker) {
        return true;
    }

    const plugins = props.config?.plugins;

    if (Array.isArray(plugins)) {
        return plugins.some(
            (p) =>
                p?.constructor?.name === 'monthSelectPlugin' ||
                (typeof p === 'object' &&
                    p !== null &&
                    'monthSelectPlugin' in (p.config || {}))
        );
    }

    return false;
});

const dayjsFormat = computed(() => {
    return flatpickrFormatToDayjs(props.config?.dateFormat);
});

const displayFormat = computed(() => {
    if (isMonthPicker.value) {
        return 'MMMM yyyy';
    }

    return dayjsFormat.value
        .replace(/YYYY/g, 'yyyy')
        .replace(/YY/g, 'yy')
        .replace(/DD/g, 'dd');
});

/**
 * Format function used by VueDatePicker for its internal display.
 */
const formatDateForDisplay = (date) => {
    if (!date) {
        return '';
    }

    if (isMonthPicker.value) {
        if (typeof date === 'object' && 'month' in date) {
            return dayjs()
                .month(date.month)
                .year(date.year)
                .format(dayjsFormat.value);
        }

        return dayjs(date).format(dayjsFormat.value);
    }

    return dayjs(date).format(dayjsFormat.value);
};

/**
 * Computed value for the input display, formatted correctly without time.
 */
const formattedInputValue = computed(() => {
    if (!internalDate.value) {
        return '';
    }

    return formatDateForDisplay(internalDate.value);
});

const resolvedMinDate = computed(() => resolveDateRef(props.config?.minDate));
const resolvedMaxDate = computed(() => resolveDateRef(props.config?.maxDate));

const filteredAttrs = computed(() => {
    const { class: _class, style: _style, ...rest } = attrs;

    const excluded = [
        'config',
        'modelValue',
        'onOn-change',
        'onOnChange',
        'on-change',
        'onOn-open',
        'on-open',
        'onOn-close',
        'on-close',
    ];

    return Object.fromEntries(
        Object.entries(rest).filter(([key]) => !excluded.includes(key))
    );
});

const inputAttrs = computed(() => {
    const { class: cls, style, id, name, placeholder, disabled } = attrs;
    const result = {};

    if (cls) {
        result.class = cls;
    }

    if (style) {
        result.style = style;
    }

    if (id) {
        result.id = id;
    }

    if (name) {
        result.name = name;
    }

    if (placeholder) {
        result.placeholder = placeholder;
    }

    if (disabled !== undefined) {
        result.disabled = disabled;
    }

    return result;
});

/**
 * Parse the modelValue into a Date the datepicker understands.
 */
const parseModelValue = (val) => {
    if (!val || val === '') {
        return null;
    }

    if (isMonthPicker.value) {
        if (typeof val === 'object' && val !== null && 'month' in val) {
            return val;
        }

        const parsed = dayjs(val, dayjsFormat.value, true);

        if (parsed.isValid()) {
            return { month: parsed.month(), year: parsed.year() };
        }

        const fallback = dayjs(val);

        if (fallback.isValid()) {
            return { month: fallback.month(), year: fallback.year() };
        }

        return null;
    }

    if (val instanceof Date) {
        return val;
    }

    const parsed = dayjs(val, dayjsFormat.value, true);

    if (parsed.isValid()) {
        return parsed.toDate();
    }

    const fallback = dayjs(val);

    if (fallback.isValid()) {
        return fallback.toDate();
    }

    return null;
};

/**
 * Format a Date back to the string format consumers expect.
 */
const formatToModelValue = (val) => {
    if (!val) {
        return '';
    }

    if (isMonthPicker.value) {
        if (typeof val === 'object' && 'month' in val) {
            return dayjs()
                .month(val.month)
                .year(val.year)
                .format(dayjsFormat.value);
        }

        return dayjs(val).format(dayjsFormat.value);
    }

    if (val instanceof Date) {
        return dayjs(val).format(dayjsFormat.value);
    }

    return val;
};

const internalDate = ref(parseModelValue(props.modelValue));

watch(
    () => props.modelValue,
    (newVal) => {
        const parsed = parseModelValue(newVal);
        const currentFormatted = formatToModelValue(internalDate.value);
        const newFormatted = formatToModelValue(parsed);

        if (currentFormatted !== newFormatted) {
            internalDate.value = parsed;
        }
    }
);

const onDateChange = (val) => {
    const formatted = formatToModelValue(val);

    emit('update:modelValue', formatted);

    let dateObj = null;

    if (val instanceof Date) {
        dateObj = val;
    } else if (val && typeof val === 'object' && 'month' in val) {
        dateObj = new Date(val.year, val.month);
    } else if (val) {
        dateObj = new Date(val);
    }

    emit(
        'on-change',
        dateObj && !isNaN(dateObj) ? [dateObj] : [],
        formatted,
        null
    );
};

onMounted(() => {
    isMounted.value = true;
});

const _flatpickr = computed(() => ({
    setDate(dateStr) {
        const parsed = parseModelValue(dateStr);
        internalDate.value = parsed;
        onDateChange(parsed);
    },
    clear() {
        internalDate.value = null;
        onDateChange(null);
    },
}));

const getElem = () => inputRef.value;

defineExpose({ _flatpickr, getElem });
</script>
